/**
 * Tiny pub-sub for "the database changed, anything caching DB reads
 * should refresh." Used by the desktop sidebar so completing/skipping a
 * task in the main column refreshes the Past Tasks widget without
 * waiting for a route change.
 *
 * Why not a full state library: 95% of the app reads on focus/mount and
 * already refreshes naturally. The desktop sidebar is the one component
 * that lives at root and has to react to writes happening on screens
 * below it. A 30-line emitter beats pulling in another dep.
 */

import { useEffect, useState } from 'react';

type Listener = (revision: number) => void;

let revision = 0;
const listeners = new Set<Listener>();

/**
 * Call after any DB write that should make caching components reload.
 * Cheap — emits to all subscribers synchronously.
 */
export function bumpDataRevision(): void {
  revision += 1;
  for (const fn of listeners) fn(revision);
}

/**
 * Returns a number that increments every time `bumpDataRevision()` is
 * called. Use as a useEffect dependency to trigger refetches.
 */
export function useDataRevision(): number {
  const [rev, setRev] = useState(revision);
  useEffect(() => {
    listeners.add(setRev);
    return () => { listeners.delete(setRev); };
  }, []);
  return rev;
}
